import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _, { debounce } from "lodash";

import {
  _fetchChatMessages,
  _fetchChatUsers,
  _getChatTemplate,
  _getTotalUnreadCount,
  _markChatAsRead,
  _saveChatTemplate,
  _sendNotificationToCandidate,
  _storeOfflineMessage,
  _addCandidatesFromBroadCast,
  _createChat,
  _deleteBroadCastGroup,
  _fetchBroadCastChatGrpDetails,
  _removeCandidatesfromBroadCast,
} from "@/services/chat";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

import { CONVERSATION_TYPE } from "../Constants/common";
import {
  formatChatMessages,
  formatChatUsersList,
} from "../utilities/dataFormatter";
const initialState = {
  chatUsers: [],
  isBroadCastChatModal: false,
  chatSearch: "",
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  chatBoxes: [],
  typingUsers: {},
  showChatPreviewModal: false,
  chatPreviewId: null,
  chatPreviewImages: [],
  chatPreviewImage: null,
  chatPreviewImageTime: null,
  totalUnreadCount: 0,
  typingObj: {},
  chatUsersLoading: false,

  // template
  clickedTemplate: null,
  showCreateChatTemplateModal: false,
  showEditChatTemplateModal: false,
  chatTemplateObj: {
    id: "",
    name: "",
    message: "",
  },
  chatTemplates: [],
  // chat type
  showCreateChat: false,
  creatingChatFor: null,
  selectedCandidateForChat: [],
  onlyCandidateSearch: "",
  onlyCandidateLoading: false,
  onlyCandidateList: [],
  onlyCandidatePagination: {
    currentPage: 1,
    isLastPage: false,
  },
  broadCastInfoPagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  chatInfo: {
    group_users: [],
  },
  showCandidateDetailModal: false,
  totalCount: 0,
  totalBroadcastInfoCount: 0,
  toggleSelected: false,
  toggleBroadCastInfoSelected: false,
  isSelectedAll: false,
  isBroadcastInfoSelectedAll: false,
  selectedBroadcastInfo: [],
  broadCastInfoLoading: false,
};

export const markChatAsRead = createAsyncThunk(
  "chat/markChatAsRead",
  async (data, { getState, dispatch }) => {
    let postData = data;
    const user = getState().clientProfile.user;
    const debouncedMarkChatAsRead = debounce(async (e) => {
      const response = _markChatAsRead(data);
      response
        .then((res) => {
          const {
            data: { is_updated },
          } = res;
          // dispatch(getTotalUnreadCount());
          if (is_updated && user.client_id == postData?.receiverId) {
            dispatch(removeUnreadCountFromChat(postData.senderId));
          }
        })
        .catch((error) => {
          handleAPICatch(error);
        })
        .finally(() => {
          // console.log('finally')
        });
    }, 500);
    debouncedMarkChatAsRead();
  },
);

// This function is used when a message is received by a candidate who is not in list
export const fetchSingleCandidateChatList = createAsyncThunk(
  "chat/fetchSingleCandidateChatList",
  async (data, thunkAPI) => {
    let candidateId = data;
    const _data = {
      currentPage: 1,
      userType: "Client",
      candidateId,
    };
    const response = _fetchChatUsers(_data);
    response
      .then((res) => {
        const {
          data: { result, meta },
        } = res;
        let data = formatChatUsersList(result);
        let existingChatUsers = thunkAPI.getState().chat.chatUsers;
        if (data.length) {
          thunkAPI.dispatch(setChatUsers([...data, ...existingChatUsers]));
        }
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        //
      });
  },
);

// Fetch Chat Users
export const fetchChatUsers = createAsyncThunk(
  "chat/fetchChatUsers",
  async (data, thunkAPI) => {
    let paginationData = thunkAPI.getState().chat.pagination;
    if (_.isEmpty(data)) {
      data = {
        search: thunkAPI.getState().chat.chatSearch,
      };
    }
    if (paginationData.isLastPage) {
      return [];
    }
    const _data = {
      ...data,
      currentPage: paginationData.currentPage,
      userType: "Client",
      // candidateId: '1014'
    };
    const response = _fetchChatUsers(_data);
    response
      .then((res) => {
        const {
          data: { result, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatChatUsersList(result);
        thunkAPI.dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList =
          currentPage === 1 ? [] : _.uniq(thunkAPI.getState().chat.chatUsers);
        thunkAPI.dispatch(setChatUsers([...prevList, ...data]));
        // thunkAPI.dispatch(setOnlyCandidateList([...prevList, ...data]))
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        thunkAPI.dispatch(updateChatUsersLoading(false));
      });
    return data;
  },
);

export const getTotalUnreadCount = createAsyncThunk(
  "chat/getTotalUnreadCount",
  async (data, thunkAPI) => {
    let response = _getTotalUnreadCount();
    response
      .then((res) => {
        const { data } = res;
        thunkAPI.dispatch(setTotalUnreadCount(data.Candidate));
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {});
  },
);

export const sendOfflineMessage = createAsyncThunk(
  "chat/sendOfflineMessage",
  async (data, thunkAPI) => {
    let postData = data;
    let response = _storeOfflineMessage(postData);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        // console.log('finally')
      });
  },
);

// Fetch Chat Detail - open chat box
export const fetchChatMessages = createAsyncThunk(
  "chat/fetchChatMessages",
  async (data, thunkAPI) => {
    let ignoreExistingOpenChat = false;
    if (data.ignoreExistingOpenChat) {
      ignoreExistingOpenChat = data.ignoreExistingOpenChat;
    }
    let isChatBoxExists = false;
    let chatBoxes = thunkAPI.getState().chat.chatBoxes;
    let candidate = thunkAPI
      .getState()
      .chat.chatUsers.find((user) => user.id == data.id);
    chatBoxes.forEach((chatBox) => {
      if (chatBox.id == data.id) {
        isChatBoxExists = true;
      }
    });

    let currentChatBox = null;
    if (isChatBoxExists) {
      currentChatBox = chatBoxes.find((chatBox) => chatBox.id == data.id);
      // curer
      if (currentChatBox.pagination.isLastPage && !ignoreExistingOpenChat) {
        return;
      }
    }

    let _data = {
      ...data,
      candidate_id: data.id,
      userType: data.userType,
      page: isChatBoxExists ? currentChatBox?.pagination?.currentPage : 1,
      conversationKey: data.conversationKey || "single",
      requestFor: data?.requestFor || "",
    };
    // console.log('data', data)
    const response = _fetchChatMessages(_data);
    response
      .then((res) => {
        const {
          data: { can_detail, candidate_ids, meta, result },
        } = res;
        // const group_can = res.data.group_can || {}
        const { currentPage, lastPage, isLastPage, total } = meta;
        let modifiedRes = formatChatMessages(result, can_detail);
        const { candidateDetails, data, for_template } = modifiedRes;

        thunkAPI.dispatch(updateClickedTemplate(null));
        if (isChatBoxExists) {
          let tempCurrentChatBox = {
            ...currentChatBox,
            messages: [...currentChatBox.messages, ...data],
            pagination: {
              currentPage: currentPage + 1,
              totalPage: lastPage,
              isLastPage: isLastPage,
            },
            candidate_ids: candidate_ids || "",
            candidate_total: candidate_ids.split(",").length || "",
          };

          thunkAPI.dispatch(updateSingleChatBox(tempCurrentChatBox));
        } else {
          // temp for 2 box only
          if (chatBoxes.length == 1 && !isChatBoxExists) {
            thunkAPI.dispatch(removeChatbox(chatBoxes[0].id));
          }

          // temp for 2 box only
          let obj = {
            ...candidateDetails,
            candidate_ids: candidate_ids,
            messages: data,
            pagination: {
              currentPage: currentPage + 1,
              totalPage: lastPage,
              isLastPage: isLastPage,
            },
            isExpanded: false,
            message: "",
            for_template: candidate?.for_template
              ? candidate.for_template
              : for_template,
            group_can: res.data.group_can || {},
          };

          thunkAPI.dispatch(addChatbox(obj));
        }
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        // console.log("finally");
      });
    // return response;
  },
);

// Calling APi to Send Message Notification To Candidate
export const sendNotificationToCandidate = createAsyncThunk(
  "chat/sendNotificationToCandidate",
  async (data, thunkAPI) => {
    let postData = data;
    // return;
    let socket = thunkAPI.getState().socket.socket;
    let response = _sendNotificationToCandidate(postData.notificationData);
    response
      .then((res) => {
        const {
          data: { sender_unread_count, sender_name },
        } = res;
        let _data = {
          message_data: {
            ...postData.messageData.message_data,
            unread_count: sender_unread_count,
            client_data: null,
          },
          client_data: {
            ...postData.messageData.client_data,
            unread_count: sender_unread_count,
          },
        };
        socket.emit("send_message_to_candidate", _data);
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        // console.log('finally')
      });
  },
);

// _getChatTemplate
export const getChatTemplate = createAsyncThunk(
  "chat/saveChatTemplate",
  async (data, thunkAPI) => {
    let response = _getChatTemplate();
    response
      .then((res) => {
        const {
          data: { chat_template },
        } = res;
        thunkAPI.dispatch(updateChatTemplates(chat_template));
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {});
  },
);

//saveChat template
export const saveChatTemplate = createAsyncThunk(
  "chat/saveChatTemplate",
  async (data, thunkAPI) => {
    let postData = data._postData;
    let callback = data.callback;
    let response = _saveChatTemplate(postData);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        showSuccessToast(message);
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        data.setIsSubmitting(false);
        data.setDisabledAddBtn(false);
      });
  },
);

const handleFetchUsers = (data, dispatch, getState, obj, callback = null) => {
  const {
    pagination,
    search,
    users,
    updatePagination,
    setUsers,
    updateLoading,
  } = obj;

  const { conversation_key = "" } = data;
  let paginationData = getState().chat[pagination] || {
    currentPage: 0,
    isLastPage: false,
  };
  if (paginationData.isLastPage) {
    return;
  }
  const _data = {
    currentPage: paginationData.currentPage,
    userType: "client",
    conversationKey: "single",
    requestFor: "create_broadcast",
    search: getState().chat[search],
    conversation_key,
  };
  const response = _fetchChatUsers(_data);
  response
    .then((res) => {
      const {
        data: { result, meta },
      } = res;
      const { currentPage, lastPage, isLastPage, total } = meta;
      let data = formatChatUsersList(result);

      dispatch(updateTotalCount(total));
      dispatch(
        updatePagination({
          currentPage: currentPage + 1,
          totalPage: lastPage,
          isLastPage: isLastPage,
        }),
      );
      let prevList =
        currentPage === 1 ? [] : _.uniq(getState().chat.onlyCandidateList);
      // let prevList = oldNumRecord == 0 ? [] : _.uniq(getState().chat[onlyCandidateList]);

      dispatch(setOnlyCandidateList([...prevList, ...data]));
      callback && callback();
    })
    .catch((error) => {
      handleAPICatch(error);
    })
    .finally(() => {
      dispatch(updateLoading(false));
    });
};

export const fetchCandidateForChat = createAsyncThunk(
  "chat/fetchCandidateForChat",
  async (data, { getState, dispatch }) => {
    let obj = {
      pagination: "onlyCandidatePagination",
      search: "onlyCandidateSearch",
      users: "onlyCandidateList",
      updatePagination: updateOnlyCandidatePagination,
      setUsers: setOnlyCandidateList,
      updateLoading: updateOnlyCanidateLoading,
    };

    handleFetchUsers(
      {
        conversation_key: CONVERSATION_TYPE.CANDIDATE,
        ...data,
      },
      dispatch,
      getState,
      obj,
    );
    return data;
  },
);

export const createChat = createAsyncThunk(
  "chat/createChat",
  async ({ isLoading, setLoading, data, callback }, { getState, dispatch }) => {
    if (isLoading) return; // Prevent duplicate requests

    try {
      setLoading(true);
      const response = await _createChat(data);

      const { id, message } = response;
      showSuccessToast(message);
      const existingChatUsers = getState().chat.chatUsers;

      // Prepare the new chat data
      const resData = {
        ...response,
        chat_type: "broadcast",
        can_name: " ",
        chat: {
          conversation_key: "broadcast",
        },
      };
      // Dispatch actions to update the state
      dispatch(setChatUsers([resData, ...existingChatUsers]));
      dispatch(
        fetchChatMessages({
          userType: "client",
          id,
          conversationKey: "broadcast",
          fromList: true,
        }),
      );
      callback && callback();
      dispatch(resetOnlyBroadCastCandidateData());
    } catch (error) {
      handleAPICatch(error);
    } finally {
      setLoading(false);
    }
  },
);

// fetch single broadcast info
export const fetchBroadCastChatGrpDetails = createAsyncThunk(
  "chat/fetchBroadCastChatGrpDetails",
  async (data, { dispatch, getState }) => {
    let paginationData = getState().chat.broadCastInfoPagination;
    let chatBoxes = getState().chat.chatBoxes;
    // if (_.isEmpty(data)) {
    //   data = {
    //     search: getState().chat.chatSearch,
    //   };
    // }
    if (paginationData.isLastPage) {
      return [];
    }
    const _data = {
      ...data,
      page: paginationData.currentPage,
      // userType: "Client",
      // candidateId: '1014'
    };
    let chatInfo = getState().chat.chatInfo;
    let response = _fetchBroadCastChatGrpDetails(_data);

    response
      .then((res) => {
        const {
          data: { result, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        // let _data = result;

        dispatch(updateTotalBroadCastInfoCount(totalCount));
        dispatch(
          updateBroadCastPagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        dispatch(
          updateSingleChatBox({
            ...chatBoxes,
            candidate_total: totalCount,
          }),
        );
        const { group_users } = chatInfo;
        // let updatedGroupUsers = [...group_users, ...result.group_users];
        let prevList =
          currentPage === 1 ? [] : _.uniq(getState().chat.chatInfo.group_users);
        // dispatch(updateChatInfo([...prevList, ...result]));

        dispatch(
          updateChatInfo({
            ...result,
            group_users: [...prevList, ...(result.group_users || [])],
          }),
        );
        dispatch(toggleChatDetailsModal(true));
        // const {
        //   data,
        // } = res;
        // dispatch(updateChatInfo(data));
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        dispatch(updateBroadcastInfoLoading(false));
      });
  },
);

// add candidate to broadcast chat
export const addCandidatesFromBroadCast = createAsyncThunk(
  "chat/addCandidatesFromBroadCast",
  async (data, { getState, dispatch }) => {
    let callback = data.callback || null;
    if (callback) {
      delete data.callback;
    }
    let postData = data;
    if (data?.loading) return;
    data?.setLoading(true);
    let response = _addCandidatesFromBroadCast(postData);
    response
      .then((res) => {
        const {
          data: { data, message },
        } = res;
        showSuccessToast(message);
        const { candidate_ids } = postData;
        let selectedCandidateForChat = getState().chat.selectedCandidateForChat;
        let newAddedCandidates = selectedCandidateForChat.map((usr) => {
          return {
            user_id: usr.id,
            user_name: usr.can_name,
            user_mobile_no: "",
            profile_image: usr.profile_title,
            user_type: "Candidate",
            role: usr.can_role,
            reference_no: usr.can_refno,
          };
        });
        let chatBoxes = getState().chat.chatBoxes;
        let updatedCandidateIds = [];
        // Ensure chatBoxes is an array
        if (Array.isArray(chatBoxes) && chatBoxes.length > 0) {
          let chatDetailCandidateIds = chatBoxes[0].candidate_ids;
          updatedCandidateIds = [
            ...chatDetailCandidateIds.split(","), // Ensure this is a string before splitting
            ...candidate_ids.split(","),
          ];
        } else {
          console.error("chatBoxes is not an array or is empty:", chatBoxes);
        }

        let chatInfo = getState().chat.chatInfo;
        const { group_users } = chatInfo;
        let updatedGroupUsers = [...group_users, ...newAddedCandidates];
        dispatch(
          updateChatInfo({
            ...chatInfo,
            group_users: updatedGroupUsers,
          }),
        );
        dispatch(
          updateSingleChatBox({
            ...chatBoxes[0],
            candidate_ids: updatedCandidateIds.join(","),
          }),
        );

        callback && callback();
        dispatch(resetOnlyBroadCastCandidateData());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        data?.setLoading(false);
      });
  },
);

// Delete candidate from broadcast chat
export const removeCandidatesFromBroadCast = createAsyncThunk(
  "chat/removeCandidatesFromBroadCast",
  async (data, { getState, dispatch }) => {
    // return;
    let callback = data.callback || null;
    if (callback) {
      delete data.callback;
    }
    let postData = {
      group_id: data?.group_id,
      candidate_ids: data.candidate_ids,
      ignoreIds: data?.ignoreIds,
    };
    if (data?.isSelectedAll) {
      postData.isSelectedAll = data.isSelectedAll;
    }
    if (data?.loading) return;
    data?.setLoading(true);
    let response = _removeCandidatesfromBroadCast(postData);
    response
      .then((res) => {
        const {
          data: { data, message },
        } = res;
        showSuccessToast(message);

        // const { candidate_ids, group_id } = postData;
        // const candidateIds = candidate_ids.split(",");
        // let chatInfo = getState().chat.chatInfo;
        // const { group_users } = chatInfo;
        // let updatedGroupUsers = group_users.filter(
        //   (user) => !candidateIds.includes(user.user_id.toString())
        // );
        // let chatBoxes = getState().chat.chatBoxes;
        // let chatDetailCandidateIds = [chatBoxes[0].candidate_ids];
        // let chatRemoveBroaddCastCandidateIds = [chatBoxes[0].candidate_ids];
        // // if (!Array.isArray(chatDetailCandidateIds)) {
        // //   chatDetailCandidateIds = Object.values(chatDetailCandidateIds);
        // // }

        // let splitChatDetailCandidateIds = chatDetailCandidateIds[0].split(',');

        // // Filter out the IDs present in candidateIds
        // let splitupdatedCandidateIdsForChatBox = splitChatDetailCandidateIds.filter(
        //   (id) => !candidateIds.includes(id)
        // );
        // if (Array.isArray(splitupdatedCandidateIdsForChatBox) && splitupdatedCandidateIdsForChatBox?.length == 0) {
        //   dispatch(deleteBroadCastGroupData({ group_id, loading: data?.deleteBroadCastLoading, setLoading: data?.setDeleteBroadCastLoading, ignoreToast: true }));
        // }

        // dispatch(
        //   updateSingleChatBox({
        //     ...chatBoxes[0],
        //     candidate_ids: splitupdatedCandidateIdsForChatBox?.join(","),
        //   })
        // );

        // dispatch(
        //   updateChatInfo({
        //     ...chatInfo,
        //     group_users: updatedGroupUsers,
        //   })
        // );
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        data?.setLoading(false);
        // console.log('finally')
      });
  },
);

export const deleteBroadCastGroupData = createAsyncThunk(
  "chat/deleteGroup",
  async (data, { getState, dispatch }) => {
    let ignoreToast = data.ignoreToast || false;
    if (ignoreToast) {
      delete data.ignoreToast;
    }
    let postData = {
      group_id: data?.group_id,
    };

    // if (data?.loading) return;
    // data?.setLoading(true);
    let response = _deleteBroadCastGroup(postData);
    response
      .then((res) => {
        const {
          data: { data, message },
        } = res;
        const { group_id } = postData;
        if (!ignoreToast) {
          showSuccessToast(message);
        }
        dispatch(toggleChatDetailsModal(false));
        setTimeout(() => {
          dispatch(updateChatInfo({}));
        }, 500);
        // dispatch(updateSingleChatBox({}));
        let existingChatUsers = getState().chat.chatUsers;

        let updatedChatUsers = existingChatUsers.filter(
          (user) => user.id != group_id || user.user_type == "Candidate",
        );
        dispatch(setChatUsers(updatedChatUsers));
        dispatch(removeChatbox(group_id));
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        data?.setLoading(false);
      });
  },
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChatUsers: (state, action) => {
      state.chatUsers = action.payload;
    },
    setChatOpen: (state, action) => {
      state.isChatOpen = action.payload;
    },
    setisBroadCastChatModal: (state, action) => {
      state.isBroadCastChatModal = action.payload;
    },
    setChatSearch: (state, action) => {
      state.chatSearch = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    addChatbox: (state, action) => {
      let tempChatBoxes = [...state.chatBoxes];
      if (
        tempChatBoxes.filter((chatBox) => chatBox.id === action.payload.id)
          .length === 0
      ) {
        state.chatBoxes = [...tempChatBoxes, action.payload];
      }
    },
    removeChatbox: (state, action) => {
      state.chatBoxes = state.chatBoxes.filter(
        (chatBox) => chatBox.id != action.payload,
      );
    },
    updateSingleChatBox: (state, action) => {
      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === action.payload.id) {
          return action.payload;
        }
        return chatBox;
      });
      state.chatBoxes = tempChatBoxes;
    },
    updateChatTypingMessage: (state, action) => {
      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === action.payload.id) {
          return {
            ...chatBox,
            message: action.payload.message,
          };
        }
        return chatBox;
      });
      state.chatBoxes = tempChatBoxes;
    },
    updateTypingStatus: (state, action) => {
      // new typing approch
      const { sender_id, typing } = action.payload;
      let tempObj = _.cloneDeep(state.typingObj);
      if (!tempObj[sender_id]) {
        tempObj = {
          ...tempObj,
          [sender_id]: 1,
        };
      } else {
        let current = tempObj[sender_id];
        tempObj = {
          ...tempObj,
          [sender_id]: typing ? current + 1 : current - 1,
        };
      }
      state.typingObj = tempObj;

      // old typing approch

      // let tempChatUsers = state.chatUsers.map((chatUser) => {
      //   if (chatUser.id === action.payload.sender_id) {
      //     let currentCount = chatUser.typingCount;
      //     return {
      //       ...chatUser,
      //       is_typing: action.payload.typing,
      //       typingCount: action.payload.typing
      //         ? currentCount + 1
      //         : currentCount - 1,
      //     };
      //   }
      //   return chatUser;
      // });
      // let tempChatBoxes = state.chatBoxes.map((chatBox) => {
      //   if (chatBox.id === action.payload.sender_id) {
      //     let currentCount = chatBox.typingCount;
      //     return {
      //       ...chatBox,
      //       is_typing: action.payload.typing,
      //       typingCount: action.payload.typing
      //         ? currentCount + 1
      //         : currentCount - 1,
      //     };
      //   }
      //   return chatBox;
      // });
      // state.chatUsers = tempChatUsers;
      // state.chatBoxes = tempChatBoxes;
    },

    // calls when new message sent
    newMessageSent: (state, action) => {
      let ignoreSorting = action.payload.ignoreSorting ? true : false;
      if (action.payload.ignoreSorting) {
        delete action.payload.ignoreSorting;
      }

      let newData = action.payload;
      let data = {
        ...newData,
        receiver_id: parseInt(newData.receiver_id),
        sender_id: parseInt(newData.sender_id),
        message_id: parseInt(newData.message_id),
      };
      let isChatBoxExists = state.chatBoxes.some(
        (chatBox) => chatBox.id === data.receiver_id,
      );
      let tempChatBoxes = state.chatBoxes;
      if (isChatBoxExists) {
        const chatBoxObj = {
          id: data.message_id,
          sender_id: data.sender_id,
          receiver_id: data.receiver_id,
          message: data.message,
          message_type: data.message_type,
          original_filename: data.original_filename
            ? data.original_filename
            : "",
          filename: data.filename ? data.filename : "",
          file_url: data.url ? data.url : "",
          file_found: data.file_found ? data.file_found : "",
          status: "Sent",
          is_deleted: "0",
          send_from: "Client",
          send_to: "Candidate",
          created_at: data.created_at,
        };
        tempChatBoxes = state.chatBoxes.map((chatBox) => {
          if (chatBox.id === data.receiver_id) {
            return {
              ...chatBox,
              messages: [chatBoxObj, ...chatBox.messages],
            };
          }
          return chatBox;
        });
      }
      let tempChatUsers = state.chatUsers.map((chatUser) => {
        if (chatUser.id === data.receiver_id) {
          return {
            ...chatUser,
            chat: data,
            last_message: data.message,
          };
        }
        return chatUser;
      });
      if (!ignoreSorting) {
        const matchingIndex = tempChatUsers.findIndex(
          (chatUser) => chatUser.id === data.receiver_id,
        );
        if (matchingIndex !== -1) {
          const matchingItem = tempChatUsers.splice(matchingIndex, 1)[0];
          tempChatUsers.unshift(matchingItem);
        }
      }
      state.chatUsers = tempChatUsers;
      state.chatBoxes = tempChatBoxes;
    },

    // calls when new message received
    newMessageReceived: (state, action) => {
      let newData = action.payload;
      let data = {
        ...newData,
        receiver_id: parseInt(newData.receiver_id),
        sender_id: parseInt(newData.sender_id),
        message_id: parseInt(newData.message_id),
      };
      let isChatBoxExists = state.chatBoxes.some(
        (chatBox) => chatBox.id === data.sender_id,
      );
      let tempChatBoxes = state.chatBoxes;
      if (isChatBoxExists) {
        const chatBoxObj = {
          id: data.message_id,
          sender_id: data.sender_id,
          receiver_id: data.receiver_id,
          message: data.message,
          message_type: data.message_type,
          original_filename: data.original_filename
            ? data.original_filename
            : "",
          status: "Sent",
          is_deleted: "0",
          send_from: "Candidate",
          send_to: "Client",
          file_found: 1,
          file_url: data.fileurl ? data.fileurl : "",
          created_at: data.created_at,
          filename: data.fileurl ? data.fileurl : "",
        };
        tempChatBoxes = state.chatBoxes.map((chatBox) => {
          if (chatBox.id === data.sender_id) {
            return {
              ...chatBox,
              messages: [chatBoxObj, ...chatBox.messages],
              unread_count: chatBox.unread_count + 1,
            };
          }
          return chatBox;
        });
      }

      let tempChatUsers = state.chatUsers.map((chatUser) => {
        if (chatUser.id === data.sender_id) {
          return {
            ...chatUser,
            chat: data,
            unread_count: chatUser.unread_count + 1,
            last_message: data.message,
          };
        }
        return chatUser;
      });

      const matchingIndex = tempChatUsers.findIndex(
        (chatUser) => chatUser.id === data.sender_id,
      );
      if (matchingIndex !== -1) {
        const matchingItem = tempChatUsers.splice(matchingIndex, 1)[0];
        tempChatUsers.unshift(matchingItem);
      }
      state.chatBoxes = tempChatBoxes;
      state.chatUsers = tempChatUsers;
    },
    // toggle expand view of chat box
    toggleExpandView: (state, action) => {
      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === action.payload) {
          return {
            ...chatBox,
            isExpanded: !chatBox.isExpanded,
          };
        }
        return chatBox;
      });
      state.chatBoxes = tempChatBoxes;
    },
    // reset chat module
    resetChatModule: (state) => {
      state.chatUsers = [];
      state.isChatOpen = false;
      state.chatSearch = "";
      state.pagination = {
        currentPage: 1,
        totalPage: 1,
        isLastPage: false,
      };
      state.chatBoxes = [];
    },
    toggleChatPreviewModal: (state, action) => {
      state.showChatPreviewModal = action.payload;
    },
    updateChatPreviewId: (state, action) => {
      state.chatPreviewImage = action.payload;
      // const chatId = action.payload.id;
      // state.chatPreviewId = chatId
      // state.chatPreviewImageTime = action.payload.time
      // let previewImages = state.chatBoxes
      //   .find((chatBox) => chatBox.id === chatId)
      //   ?.messages.filter(
      //     (message) =>
      //       message.message_type.toLowerCase() === "image" &&
      //       message.file_found === 1,
      //   );
      // state.chatPreviewImages = _.reverse(previewImages);
    },
    // remove unread count from chat user list
    removeUnreadCountFromChat: (state, action) => {
      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === action.payload) {
          return {
            ...chatBox,
            unread_count: 0,
          };
        }
        return chatBox;
      });
      let tempChatUsers = state.chatUsers.map((chatUser) => {
        if (chatUser.id === action.payload) {
          return {
            ...chatUser,
            unread_count: 0,
          };
        }
        return chatUser;
      });
      state.chatUsers = tempChatUsers;
      state.chatBoxes = tempChatBoxes;
    },
    // calls when user read the message
    updateReadReceipt: (state, action) => {
      let receiver_id = parseInt(action.payload.receiver_id);

      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === receiver_id) {
          return {
            ...chatBox,
            // unread_count: 0,
            messages: chatBox.messages.map((message) => {
              if (message.receiver_id === receiver_id) {
                return {
                  ...message,
                  status: "Seen",
                };
              }
              return message;
            }),
          };
        }
        return chatBox;
      });
      let tempChatUsers = state.chatUsers.map((chatUser) => {
        if (chatUser.id === receiver_id) {
          return {
            ...chatUser,
            chat: {
              ...chatUser.chat,
              status: "Seen",
            },
          };
        }
        return chatUser;
      });
      state.chatBoxes = tempChatBoxes;
      state.chatUsers = tempChatUsers;
    },
    setTotalUnreadCount: (state, action) => {
      state.totalUnreadCount = action.payload;
    },
    updateChatUsersLoading: (state, action) => {
      state.chatUsersLoading = action.payload;
    },

    updateClickedTemplate: (state, action) => {
      state.clickedTemplate = action.payload;
    },
    toggleCreateChatTemplateModal: (state, action) => {
      state.showCreateChatTemplateModal = action.payload;
    },
    toggleEditChatTemplateModal: (state, action) => {
      state.showEditChatTemplateModal = action.payload;
    },
    updateChatTemplateObj: (state, action) => {
      state.chatTemplateObj = {
        ...state.chatTemplateObj,
        ...action.payload,
      };
    },
    updateChatTemplates(state, action) {
      state.chatTemplates = action.payload;
    },
    updateInitiateStatusInChatBox(state, action) {
      const chatBoxId = action.payload;
      let tempChatBoxes = state.chatBoxes.map((chatBox) => {
        if (chatBox.id === chatBoxId) {
          return {
            ...chatBox,
            messages: chatBox.messages.map((message) => {
              return {
                ...message,
                initiate_status: "initiated",
              };
            }),
          };
        }
        return chatBox;
      });
      state.chatBoxes = tempChatBoxes;
    },
    toggleCreateChat: (state, action) => {
      state.showCreateChat = action.payload;
    },
    updateCratingChatFor: (state, action) => {
      state.creatingChatFor = action.payload;
    },
    updateSelectedCandidateForChat: (state, action) => {
      state.selectedCandidateForChat = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateBroadcastInfoSelectedCandidateForChat: (state, action) => {
      state.selectedBroadcastInfo = action.payload;
      if (action.payload.length == 0) {
        state.isBroadcastInfoSelectedAll = false;
      }
    },

    setOnlyCandidateSearch: (state, action) => {
      state.onlyCandidateSearch = action.payload;
    },
    updateOnlyCandidatePagination: (state, action) => {
      state.onlyCandidatePagination = {
        ...state.onlyCandidatePagination,
        ...action.payload,
      };
    },
    updateOnlyCanidateLoading: (state, action) => {
      state.onlyCandidateLoading = action.payload;
    },
    setOnlyCandidateList: (state, action) => {
      state.onlyCandidateList = action.payload;
    },
    resetOnlyBroadCastCandidateData: (state) => {
      state.onlyCandidateList = [];
      state.onlyCandidatePagination = {
        currentPage: 1,
        isLastPage: false,
      };
      // state.onlyCandidateLoading = false;
      state.selectedCandidateForChat = [];
      // state.chatInfo = [];
      state.selectedBroadcastInfo = [];
      state.onlyCandidateSearch = "";
      state.isSelectedAll = false;
      state.isBroadcastInfoSelectedAll = false;
      state.toggleSelected = false;
      state.toggleBroadCastInfoSelected = false;
    },

    // onclick- chat group details show
    updateChatInfo: (state, action) => {
      state.chatInfo = {
        ...state.chatInfo,
        ...action.payload,
      };
    },
    toggleChatDetailsModal: (state, action) => {
      state.showCandidateDetailModal = action.payload;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    updateTotalBroadCastInfoCount: (state, action) => {
      state.totalBroadcastInfoCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    toggleBroadCastInfoSelectedAll: (state, action) => {
      state.isBroadcastInfoSelectedAll = action.payload;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateBroadCastInfoToggleSelected: (state, action) => {
      state.toggleBroadCastInfoSelected = !state.toggleBroadCastInfoSelected;
    },

    updateBroadCastPagination: (state, action) => {
      state.broadCastInfoPagination = {
        ...state.broadCastInfoPagination,
        ...action.payload,
      };
    },

    updateBroadcastInfoLoading: (state, action) => {
      state.broadCastInfoLoading = action.payload;
    },
  },
});

export const {
  setChatUsers,
  setChatOpen,
  setisBroadCastChatModal,
  setChatSearch,
  updatePagination,
  addChatbox,
  removeChatbox,
  updateChatTypingMessage,
  updateTypingStatus,
  updateSingleChatBox,
  newMessageSent,
  newMessageReceived,
  toggleExpandView,
  resetChatModule,
  toggleChatPreviewModal,
  updateChatPreviewId,
  removeUnreadCountFromChat,
  updateReadReceipt,
  setTotalUnreadCount,
  updateChatUsersLoading,
  updateClickedTemplate,
  toggleCreateChatTemplateModal,
  toggleEditChatTemplateModal,
  updateChatTemplateObj,
  updateChatTemplates,
  updateInitiateStatusInChatBox,
  setCallback,
  toggleCreateChat,
  updateCratingChatFor,
  updateSelectedCandidateForChat,
  setOnlyCandidateSearch,
  updateOnlyCandidatePagination,
  updateOnlyCanidateLoading,
  setOnlyCandidateList,
  resetOnlyBroadCastCandidateData,
  updateChatInfo,
  toggleChatDetailsModal,
  updateTotalCount,
  updateTotalBroadCastInfoCount,
  toggleSelectedAll,
  toggleBroadCastInfoSelectedAll,
  updateToggleSelected,
  updateBroadCastInfoToggleSelected,
  updateBroadcastInfoSelectedCandidateForChat,
  updateBroadCastPagination,
  updateBroadcastInfoLoading,
} = chatSlice.actions;

export default chatSlice.reducer;
